// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Simple view router for the SPA.
 *
 * Uses either a query parameter (default "view") or the hash fragment to
 * keep the current view in the URL, and the History API to manage
 * navigation without full page reloads.
 *
 * Usage example (query-string mode, the default):
 *
 *   const router = new ViewRouter({
 *     mode: 'query',              // or 'hash'
 *     viewParamName: 'view',      // optional, defaults to 'view'
 *     homeViewValue: 'home',      // optional, defaults to 'home'
 *     basePath: '/',              // optional, defaults to '/'
 *     clearQueryParams: true,     // optional, defaults to true
 *     preservedParams: ['view'],  // optional list of other query params to keep
 *   });
 *
 *   const initialView = router.getCurrentViewFromUrl();
 *   router.onViewChange(view => renderView(view));
 *   router.normalizeCurrentUrl(initialView);
 *   router.start();
 *   renderView(initialView);
 *
 * URL normalization rules:
 * - In both modes, the path is forced to basePath and any extra path
 *   segments are removed.
 * - In "query" mode:
 *   - If clearQueryParams is true (default): all existing query parameters
 *     are cleared first, then only the view parameter and preservedParams
 *     are written back.
 *   - If clearQueryParams is false: existing query parameters remain, and
 *     the view parameter is added/updated; preservedParams has no effect
 *     in this case.
 *   - The hash fragment is left untouched.
 * - In "hash" mode:
 *   - If clearQueryParams is true (default): all existing query parameters
 *     are cleared first, then only preservedParams are written back.
 *   - If clearQueryParams is false: existing query parameters remain, and
 *     preservedParams has no effect.
 *   - The hash fragment is treated like a query string and the router only
 *     updates the view parameter; other hash parameters are preserved.
 *
 * Click interception rules:
 * - Any <a> element with the data-view-nav attribute is treated as an
 *   in-app navigation link. The router:
 *   - intercepts plain left-clicks (no modifier keys) on such links,
 *   - prevents the browser's default navigation, and
 *   - reads the target view from the link's href (via the configured
 *     viewParamName in either the query string or hash, depending on mode).
 * - Links with target != "_self" or with a download attribute are never
 *   intercepted and are left to the browser.
 *
 * Responsibilities:
 * - Derive the current view from the URL via the configured parameter/hash
 * - Normalize URLs to a canonical SPA form (base path + whitelisted params)
 * - Push/replace history entries when navigating
 * - Intercept in-app navigation clicks
 * - Notify listeners when the active view changes
 */

/** Listener invoked whenever the active view changes. */
export type ViewChangeListener = (view: string) => void;

/**
 * Configuration for ViewRouter.
 */
export interface ViewRouterConfig {
  /**
   * Determines how the view is encoded in the URL.
   * - 'query' (default): uses ?view=...
   * - 'hash' : uses #view=...
   */
  mode?: "query" | "hash";
  /** Name of the query parameter holding the active view id. Defaults to "view". */
  viewParamName?: string;
  /** Logical name of the home view; equivalent to the absence of the view param. Defaults to "home". */
  homeViewValue?: string;
  /** SPA base path. All normalized URLs will use this path. Defaults to "/". */
  basePath?: string;
  /**
   * When true (default), all query parameters not listed in preservedParams
   * are cleared during normalization. When false, existing query parameters
   * are left untouched.
   */
  clearQueryParams?: boolean;
  /** Additional query parameters to preserve when normalizing URLs. */
  preservedParams?: string[];
  /** Query parameters owned by each view, in their generated URL order. */
  viewQueryParams?: Record<string, readonly string[]>;
}

/**
 * View router that keeps the current view in the URL and notifies listeners
 * when it changes.
 */
export class ViewRouter {
  readonly #listeners = new Set<ViewChangeListener>();

  readonly #viewParamName: string;
  readonly #homeViewValue: string;
  readonly #basePath: string;
  readonly #preservedParams: readonly string[];
  readonly #mode: "query" | "hash";
  readonly #clearQueryParams: boolean;
  readonly #viewQueryParams: Record<string, readonly string[]>;

  constructor(config: ViewRouterConfig = {}) {
    this.#viewParamName = config.viewParamName ?? "view";
    this.#homeViewValue = config.homeViewValue ?? "home";
    this.#basePath = config.basePath ?? "/";
    this.#preservedParams = config.preservedParams ?? [];
    this.#mode = config.mode ?? "query";
    this.#clearQueryParams = config.clearQueryParams ?? true;
    this.#viewQueryParams = config.viewQueryParams ?? {};
  }

  /** Returns the current view id from the URL, defaulting to the configured home view. */
  getCurrentViewFromUrl(): string {
    const u = new URL(window.location.href);

    if (this.#mode === "hash") {
      const raw = u.hash.startsWith("#") ? u.hash.slice(1) : u.hash;
      const params = new URLSearchParams(raw);
      const value = params.get(this.#viewParamName)?.trim();
      return value || this.#homeViewValue;
    }

    const value = u.searchParams.get(this.#viewParamName)?.trim();
    return value || this.#homeViewValue;
  }

  /** Starts listening to popstate and click events. */
  start(): void {
    window.addEventListener("popstate", this.handlePopState);
    document.addEventListener("click", this.handleDocumentClick, { capture: true });
  }

  /** Adds a listener that is notified whenever the active view changes. */
  onViewChange(listener: ViewChangeListener): void {
    this.#listeners.add(listener);
  }

  /**
   * Normalizes the current URL to the canonical SPA format.
   *
   * Currently this means:
   * - path is always the configured basePath
   * - only the view and whitelisted query parameters are present
   */
  normalizeCurrentUrl(view: string, opts: { preserveParams?: readonly string[] } = {}): void {
    const current = new URL(window.location.href);
    const url = new URL(window.location.href);
    url.pathname = this.#basePath;
    const preservedParams = new Set([...this.#preservedParams, ...(opts.preserveParams ?? [])]);

    const normalizedView = view && view !== this.#homeViewValue ? view : this.#homeViewValue;

    if (this.#mode === "hash") {
      // Preserve whitelisted query params; encode view in hash.
      if (this.#clearQueryParams) {
        url.search = "";
        for (const name of preservedParams) {
          const values = current.searchParams.getAll(name);
          for (const v of values) {
            url.searchParams.append(name, v);
          }
        }
      }

      const rawHash = current.hash.startsWith("#") ? current.hash.slice(1) : current.hash;
      const hashParams = new URLSearchParams(rawHash);
      if (normalizedView !== this.#homeViewValue) {
        hashParams.set(this.#viewParamName, normalizedView);
      } else {
        hashParams.delete(this.#viewParamName);
      }
      const hash = hashParams.toString();
      url.hash = hash ? `#${hash}` : "";
    } else {
      // Query-string mode: encode view and preserved params in search.
      if (this.#clearQueryParams) {
        url.search = "";
      }
      if (normalizedView !== this.#homeViewValue) {
        url.searchParams.set(this.#viewParamName, normalizedView);
      }
      if (this.#clearQueryParams) {
        for (const name of this.#viewQueryParams[normalizedView] ?? []) {
          const value = current.searchParams.get(name);
          if (value !== null) url.searchParams.set(name, value);
        }
        for (const name of preservedParams) {
          const values = current.searchParams.getAll(name);
          for (const v of values) {
            url.searchParams.append(name, v);
          }
        }
      }
      if (
        normalizedView === "song" ||
        normalizedView === "search" ||
        normalizedView === "index" ||
        normalizedView === this.#homeViewValue
      )
        url.hash = "";
    }

    window.history.replaceState(null, "", url);
  }

  /**
   * Navigates to the given view and notifies listeners.
   *
   * If navigating to the same logical view as the current URL, uses
   * replaceState instead of pushState to avoid adding a duplicate
   * history entry.
   */
  navigateTo(view: string, opts: { hash?: string; params?: URLSearchParams } = {}): void {
    const current = new URL(window.location.href);
    const url = new URL(window.location.href);
    url.pathname = this.#basePath;

    const normalizedView = view && view !== this.#homeViewValue ? view : this.#homeViewValue;

    if (this.#mode === "hash") {
      // Preserve whitelisted query params; encode view in hash.
      if (this.#clearQueryParams) {
        url.search = "";
        for (const name of this.#preservedParams) {
          const values = current.searchParams.getAll(name);
          for (const v of values) {
            url.searchParams.append(name, v);
          }
        }
      }

      const rawHash = current.hash.startsWith("#") ? current.hash.slice(1) : current.hash;
      const hashParams = new URLSearchParams(rawHash);
      if (normalizedView !== this.#homeViewValue) {
        hashParams.set(this.#viewParamName, normalizedView);
      } else {
        hashParams.delete(this.#viewParamName);
      }
      const hash = hashParams.toString();
      url.hash = hash ? `#${hash}` : "";
    } else {
      // Query-string mode: encode view and preserved params in search.
      if (this.#clearQueryParams) {
        url.search = "";
      }
      if (normalizedView !== this.#homeViewValue) {
        url.searchParams.set(this.#viewParamName, normalizedView);
      }
      if (this.#clearQueryParams) {
        for (const name of this.#viewQueryParams[normalizedView] ?? []) {
          const value = opts.params?.get(name);
          if (value !== undefined && value !== null) url.searchParams.set(name, value);
        }
        for (const name of this.#preservedParams) {
          const values = opts.params?.has(name)
            ? opts.params.getAll(name)
            : current.searchParams.getAll(name);
          for (const v of values) {
            url.searchParams.append(name, v);
          }
        }
      }

      // Apply explicit hash from the navigation target if provided.
      // This is important for deep links like: /?view=<book>#song=<id>
      url.hash = opts.hash ?? "";
    }

    if (url.href === current.href) {
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }

    this.notify(normalizedView);
  }

  /** Notifies all registered listeners of a view change. */
  private notify(view: string): void {
    for (const listener of this.#listeners) {
      listener(view);
    }
  }

  /** Handles browser back/forward navigation. */
  private handlePopState = (): void => {
    const view = this.getCurrentViewFromUrl();
    this.normalizeCurrentUrl(view);
    this.notify(view);
  };

  /**
   * Global click handler that intercepts intra-app navigation links.
   */
  private handleDocumentClick = (e: MouseEvent): void => {
    if (e.defaultPrevented) return;
    // Only intercept normal left-click navigation.
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // e.target can be a Text node in some cases (Firefox), so normalize to an Element.
    const rawTarget = e.target as unknown;
    const el: Element | null =
      rawTarget instanceof Element
        ? rawTarget
        : rawTarget && (rawTarget as any).parentElement
          ? (rawTarget as any).parentElement
          : null;

    if (!el) return;

    const a = el.closest("a[data-view-nav]") as HTMLAnchorElement | null;
    if (!a) return;

    // Let the browser handle special links.
    if (a.target && a.target !== "_self") return;
    if (a.hasAttribute("download")) return;

    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    // Prevent default ASAP so we never trigger a full page navigation.
    e.preventDefault();

    let view: string | null;
    if (this.#mode === "hash") {
      const raw = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
      const params = new URLSearchParams(raw);
      view = params.get(this.#viewParamName);
    } else {
      view = url.searchParams.get(this.#viewParamName);
    }

    this.navigateTo(view || this.#homeViewValue, { hash: url.hash, params: url.searchParams });
  };
}
